import { GeocodingResult } from '../types'
import { initialMapCenter, extentFeat } from '../constants'

import { turf } from '../utils/index'
// Mapbox Geocoding-based implementation for address/POI search in Slovenia.
// - Language defaults to English (en)
// - Country restricted to Slovenia (SI)
// - Proximity biased to initialMapCenter (Ljubljana in this project)
// - Results adapted to existing GeocodingResult shape

type MapboxContext = {
  id: string
  text: string
  short_code?: string
}

type MapboxFeature = {
  id: string
  place_name: string
  text: string
  center: [number, number]
  place_type: string[]
  context?: MapboxContext[]
  relevance?: number
}

type MapboxResponse = {
  features: MapboxFeature[]
}

const MB_BASE =
  process.env.REACT_APP_MAPBOX_GEOCODING_URL || 'https://api.mapbox.com/geocoding/v5/mapbox.places'

const getMapboxToken = (): string => {
  // Prefer a dedicated geocoding token if provided; otherwise use the map token already present in the app
  return process.env.REACT_APP_MAPBOX_TOKEN || process.env.REACT_APP_MB_ACCESS || ''
}

const parseLocality = (feature: MapboxFeature): string => {
  const ctx = feature.context || []
  // Try to extract a place/locality from the context entries
  // e.g. place.123, locality.123, district.123
  const preferredTypes = ['place', 'locality', 'district', 'region']
  for (const t of preferredTypes) {
    const hit = ctx.find(c => c.id.startsWith(`${t}.`))
    if (hit?.text) return hit.text
  }
  return ''
}

const mapFeatureToGeocodingResult = (f: MapboxFeature): GeocodingResult => {
  const [lng, lat] = f.center
  const label = f.place_name
  const name = f.text || label
  const locality = parseLocality(f)
  const layer = f.place_type?.[0] || 'poi'
  const confidence = typeof f.relevance === 'number' ? Math.round(f.relevance * 100) / 100 : 1

  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: {
      gid: f.id,
      layer,
      source: 'mapbox',
      name,
      label,
      neighbourhood: '',
      confidence,
      distance: 0,
      locality,
      lngLat: { lng, lat },
    },
  }
}

// alias boosting removed

const dedupeByLabel = (features: GeocodingResult[]) =>
  features.filter(
    (feat, index, self) =>
      index === self.findIndex(fFeat => feat.properties.label === fFeat.properties.label),
  )

export const geocodeAddress = async (
  text: string,
  resultCount: number,
): Promise<GeocodingResult[]> => {
  const token = getMapboxToken()
  if (!token) {
    // No token available; return empty results to avoid runtime errors
    // You must set REACT_APP_MAPBOX_TOKEN or REACT_APP_MB_ACCESS for geocoding to work.
    return []
  }

  const params = new URLSearchParams({
    access_token: token,
    language: 'en,sl',
    limit: String(resultCount),
    country: 'SI',
    // Include both POIs and addresses/places to catch things like "IJS"
    types: 'poi,address,place,locality,neighborhood,district',
    autocomplete: 'true',
    fuzzyMatch: 'true',
  })

  // Bias to initial map center (Ljubljana); limit to LJ happens post-fetch by polygon filtering
  if (
    initialMapCenter &&
    typeof (initialMapCenter as any).lng === 'number' &&
    typeof (initialMapCenter as any).lat === 'number'
  ) {
    const { lng, lat } = initialMapCenter as { lng: number; lat: number }
    params.set('proximity', `${lng},${lat}`)
  }

  const typesPoi = 'poi,place,locality,neighborhood,district'
  const typesAddr = 'address,place'

  const makeUrl = (types: string, limit: number) => {
    const p = new URLSearchParams(params)
    p.set('types', types)
    p.set('limit', String(limit))
    if (types === typesPoi) {
      // Prefer POIs in education and government categories to improve building/institution matches
      p.set('categories', 'education,school,university,government')
    }
    return `${MB_BASE}/${encodeURIComponent(text)}.json?${p.toString()}`
  }

  const fetchCountPoi = Math.max(resultCount * 5, 25)
  const fetchCountAddr = Math.max(resultCount * 3, 15)

  try {
    // Phase 1: POIs and places (buildings, institutions, organizations)
    const resp1 = await fetch(makeUrl(typesPoi, fetchCountPoi))
    let results: GeocodingResult[] = []
    if (resp1.ok) {
      const data1 = (await resp1.json()) as MapboxResponse
      const mapped1 = (data1.features || []).map(mapFeatureToGeocodingResult)
      const inExtent1 = mapped1.filter(f => turf.within(f as any, extentFeat as any))
      results = dedupeByLabel(inExtent1)
    }

    // Phase 2: addresses (only if we still need more results)
    if (results.length < resultCount) {
      const resp2 = await fetch(makeUrl(typesAddr, fetchCountAddr))
      if (resp2.ok) {
        const data2 = (await resp2.json()) as MapboxResponse
        const mapped2 = (data2.features || []).map(mapFeatureToGeocodingResult)
        const inExtent2 = mapped2.filter(f => turf.within(f as any, extentFeat as any))
        const combined = dedupeByLabel([...results, ...inExtent2])
        results = combined.slice(0, resultCount)
      }
    }

    return results
  } catch {
    return []
  }
}
